import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import {
  LANDED_COST_STATUSES,
  type LandedCostFilters,
  type LandedCostStatus,
} from '@/features/purchasing-landed-costs/api'
import { LandedCostList } from '@/features/purchasing-landed-costs/landed-cost-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the landed-cost list. `landed-costs.new.tsx` and
 * `landed-costs.$landedCostId.index.tsx` are children in flat file routing,
 * so this renders the Outlet when one of them matches (mirrors `orders.tsx`).
 */
export const Route = createFileRoute('/_authenticated/purchasing/landed-costs')({
  validateSearch: (search: Record<string, unknown>): LandedCostFilters => ({
    status: isStatus(search.status) ? search.status : undefined,
    supplier: text(search.supplier),
    category: text(search.category),
    order: text(search.order),
    page: positive(search.page),
    perPage: positive(search.perPage),
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'landed_cost.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: LandedCostsRoute,
})

function LandedCostsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <LandedCostList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/purchasing/landed-costs', search: next })}
    />
  )
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined
}

function positive(value: unknown): number | undefined {
  const number = Number(value)
  return Number.isInteger(number) && number > 0 ? number : undefined
}

function isStatus(value: unknown): value is LandedCostStatus {
  return typeof value === 'string' && (LANDED_COST_STATUSES as string[]).includes(value)
}
